'use memo'

import {
  TaskCategoryTaskType,
  TaskStepDto,
} from '@urbanx/agx.activities.client'
import { Deposit, FormCondition, SpecialConditionData } from '@urbanx/agx.campaigns.client'
import { AgxCurrency } from '@urbanx/agx-ui-components'
import {
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
  AgxYesNoQuestion,
  YesNo,
} from '@urbanx/agx.ui-components2'
import dayjs from 'dayjs'
import TaskModalStepToggle from 'pages/allTasks/TaskModal/TaskModalStepToggle'
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps'
import { useEffect, useState } from 'react'
import { getTaskTypeLabel } from 'utils/tasks'
import ContractComplianceBodyCorporateRecord from './ContractComplianceBodyCorporateRecord'
import ContractComplianceBuildingAndPest from './ContractComplianceBuildingAndPest'
import ContractComplianceDeposit from './ContractComplianceDeposit'
import ContractComplianceFinanceCondition from './ContractComplianceFinanceCondition'
import ContractComplianceSettlementDate from './ContractComplianceSettlementDate'
import ContractComplianceSpecialConditions from './ContractComplianceSpecialConditions'
import {
  BALANCE_DEPOSIT_STEP_NAME,
  BODY_CORPORATE_RECORDS_STEP_NAME,
  BUILDING_AND_PEST_STEP_NAME,
  FINANCE_CONDITION_STEP_NAME,
  INITIAL_DEPOSIT_STEP_NAME,
  REQUIRED_CONTRACT_DATE_STEP_NAME,
  REQUIRED_SALE_PRICE_STEP_NAME,
  SETTLEMENT_DATE_STEP_NAME,
  isSpecialConditionsComplianceStep,
} from './ContractComplianceHelper'

const CONTRACT_DATE_REQUIRED_ERROR = 'Contract date is required'
const SALE_PRICE_REQUIRED_ERROR = 'Sale price is required'

interface ContractComplianceStepProps extends TaskModalPageProps {
  balanceDeposit?: Deposit
  bodyCorporateRecordsCondition?: FormCondition
  buildingAndPestCondition?: FormCondition
  hasSpecialConditions?: boolean
  initialDeposit?: Deposit
  settlementInfo?: FormCondition
  subjectToFinance?: FormCondition
  onContractDateUpdated?: (contractDate: Date | undefined) => void
  onDepositsUpdated?: (updated: {
    initialDeposit?: Deposit
    balanceDeposit?: Deposit
  }) => void
  onDepositValidationErrorChange?: (hasValidationError: boolean) => void
  onHasSpecialConditionsUpdated?: (hasSpecialConditions: boolean) => void
  onFinanceConditionValidationErrorChange?: (
    hasValidationError: boolean
  ) => void
  onBuildingAndPestConditionUpdated?: (
    buildingAndPestCondition: FormCondition | undefined
  ) => void
  onBuildingAndPestValidationErrorChange?: (
    hasValidationError: boolean
  ) => void
  onBodyCorporateRecordsConditionUpdated?: (
    bodyCorporateRecordsCondition: FormCondition | undefined
  ) => void
  onBodyCorporateRecordsValidationErrorChange?: (
    hasValidationError: boolean
  ) => void
  onSettlementDateValidationErrorChange?: (
    hasValidationError: boolean
  ) => void
  onSettlementInfoUpdated?: (
    settlementInfo: FormCondition | undefined
  ) => void
  onSubjectToFinanceUpdated?: (
    subjectToFinance: FormCondition | undefined
  ) => void
  onSalePriceUpdated?: (salePrice: number | undefined) => void
  onSpecialConditionsUpdated?: (
    specialConditions: SpecialConditionData[]
  ) => void
  salePrice?: number
  specialConditions?: SpecialConditionData[]
  step: TaskStepDto
}

const ContractComplianceStep = (props: ContractComplianceStepProps) => {
  const {
    contractDate,
    hasSpecialConditions,
    onContractDateUpdated,
    onHasSpecialConditionsUpdated,
    onSalePriceUpdated,
    onSpecialConditionsUpdated,
    balanceDeposit,
    bodyCorporateRecordsCondition,
    buildingAndPestCondition,
    initialDeposit,
    settlementInfo,
    onFinanceConditionValidationErrorChange,
    onBuildingAndPestConditionUpdated,
    onBuildingAndPestValidationErrorChange,
    onBodyCorporateRecordsValidationErrorChange,
    onBodyCorporateRecordsConditionUpdated,
    onDepositValidationErrorChange,
    onDepositsUpdated,
    onSettlementDateValidationErrorChange,
    onSettlementInfoUpdated,
    onSubjectToFinanceUpdated,
    salePrice,
    specialConditions = [],
    step,
    subjectToFinance,
    task,
    toggleTaskStep,
  } = props

  const isGeneralComplianceTask =
    task.category?.taskType === TaskCategoryTaskType.GeneralContractCompliance
  const isContractOfferTask =
    task.category?.taskType === TaskCategoryTaskType.ContractOffer

  const isContractDateStep =
    isGeneralComplianceTask && step.name === REQUIRED_CONTRACT_DATE_STEP_NAME
  const isSalePriceStep =
    isGeneralComplianceTask && step.name === REQUIRED_SALE_PRICE_STEP_NAME
  const isInitialDepositStep =
    isContractOfferTask && step.name === INITIAL_DEPOSIT_STEP_NAME
  const isBalanceDepositStep =
    isContractOfferTask && step.name === BALANCE_DEPOSIT_STEP_NAME
  const isFinanceConditionStep =
    isContractOfferTask && step.name === FINANCE_CONDITION_STEP_NAME
  const isBuildingAndPestStep =
    isContractOfferTask && step.name === BUILDING_AND_PEST_STEP_NAME
  const isBodyCorporateRecordsStep =
    isContractOfferTask && step.name === BODY_CORPORATE_RECORDS_STEP_NAME
  const isSettlementDateStep =
    isContractOfferTask && step.name === SETTLEMENT_DATE_STEP_NAME
  const isSpecialConditionsStep = isSpecialConditionsComplianceStep(step.name)
  const specialConditionsAnswer = hasSpecialConditions ? YesNo.Yes : YesNo.No
  const [showSpecialConditionsPicker, setShowSpecialConditionsPicker] =
    useState(hasSpecialConditions)

  useEffect(() => {
    setShowSpecialConditionsPicker(hasSpecialConditions ?? false)
  }, [hasSpecialConditions])

  useEffect(() => {
    if (!isContractDateStep) return

    const isCompleted = Boolean(contractDate)
    if ((step.isCompleted ?? false) !== isCompleted) {
      toggleTaskStep(step.order ?? 0, isCompleted)
    }
  }, [
    contractDate,
    isContractDateStep,
    step.isCompleted,
    step.order,
    toggleTaskStep,
  ])

  useEffect(() => {
    if (!isSalePriceStep) return

    const isCompleted = salePrice !== undefined && salePrice !== null
    if ((step.isCompleted ?? false) !== isCompleted) {
      toggleTaskStep(step.order ?? 0, isCompleted)
    }
  }, [isSalePriceStep, salePrice, step.isCompleted, step.order, toggleTaskStep])

  useEffect(() => {
    if (!isSpecialConditionsStep) return

    const isCompleted =
      specialConditionsAnswer === YesNo.No ||
      (specialConditionsAnswer === YesNo.Yes && specialConditions.length > 0)

    if ((step.isCompleted ?? false) !== isCompleted) {
      toggleTaskStep(step.order ?? 0, Boolean(isCompleted))
    }
  }, [
    isSpecialConditionsStep,
    specialConditions.length,
    specialConditionsAnswer,
    step.isCompleted,
    step.order,
    toggleTaskStep,
  ])

  if (isInitialDepositStep) {
    return (
      <ContractComplianceDeposit
        {...props}
        initialDeposit={initialDeposit}
        balanceDeposit={balanceDeposit}
        salePrice={salePrice}
        onDepositValidationErrorChange={onDepositValidationErrorChange}
        onDepositsUpdated={onDepositsUpdated}
      />
    )
  }

  if (isBalanceDepositStep) {
    return null
  }

  if (isFinanceConditionStep) {
    return (
      <ContractComplianceFinanceCondition
        {...props}
        subjectToFinance={subjectToFinance}
        onFinanceConditionValidationErrorChange={
          onFinanceConditionValidationErrorChange
        }
        onSubjectToFinanceUpdated={onSubjectToFinanceUpdated}
      />
    )
  }

  if (isBuildingAndPestStep) {
    return (
      <ContractComplianceBuildingAndPest
        {...props}
        buildingAndPestCondition={buildingAndPestCondition}
        onBuildingAndPestValidationErrorChange={
          onBuildingAndPestValidationErrorChange
        }
        onBuildingAndPestConditionUpdated={onBuildingAndPestConditionUpdated}
      />
    )
  }

  if (isBodyCorporateRecordsStep) {
    return (
      <ContractComplianceBodyCorporateRecord
        {...props}
        bodyCorporateRecordsCondition={bodyCorporateRecordsCondition}
        onBodyCorporateRecordsValidationErrorChange={
          onBodyCorporateRecordsValidationErrorChange
        }
        onBodyCorporateRecordsConditionUpdated={
          onBodyCorporateRecordsConditionUpdated
        }
      />
    )
  }

  if (isSettlementDateStep) {
    return (
      <ContractComplianceSettlementDate
        {...props}
        settlementInfo={settlementInfo}
        onSettlementDateValidationErrorChange={
          onSettlementDateValidationErrorChange
        }
        onSettlementInfoUpdated={onSettlementInfoUpdated}
      />
    )
  }

  if (isContractDateStep) {
    const hasContractDate = Boolean(contractDate)

    return (
      <AgxDatePicker
        id="contract-compliance-contract-date"
        stretch
        label={step.name ?? REQUIRED_CONTRACT_DATE_STEP_NAME}
        required
        validate
        errorText={hasContractDate ? undefined : CONTRACT_DATE_REQUIRED_ERROR}
        value={contractDate ? dayjs(contractDate).format('YYYY-MM-DD') : ''}
        optionalText=""
        onChange={(value) => {
          onContractDateUpdated?.(value ? new Date(value) : undefined)
        }}
      />
    )
  }

  if (isSalePriceStep) {
    const hasSalePrice = salePrice !== undefined && salePrice !== null

    return (
      <AgxCurrency
        id="contract-compliance-sale-price"
        label={step.name ?? REQUIRED_SALE_PRICE_STEP_NAME}
        required
        validate
        error={hasSalePrice ? undefined : SALE_PRICE_REQUIRED_ERROR}
        onInputValueChange={({ value: updatedValue }) => {
          onSalePriceUpdated?.(
            updatedValue ? parseFloat(updatedValue) || undefined : undefined
          )
        }}
        stretch
        defaultValue={salePrice?.toString() || ''}
        decimalPoints={2}
        displayErrors
      />
    )
  }

  if (isSpecialConditionsStep) {
    return (
      <AgxSpacer direction="vertical" fill gap="spacing-xl">
        <AgxYesNoQuestion
          id="contract-compliance-special-conditions"
          label="Are there any Special Conditions?"
          defaultValue={specialConditionsAnswer}
          onValueChanged={({ value }) => {
            const hasConditions = value === YesNo.Yes

            setShowSpecialConditionsPicker(hasConditions)
            onHasSpecialConditionsUpdated?.(hasConditions)

            if (value === YesNo.No) {
              onSpecialConditionsUpdated?.([])
            }
          }}
          required
        />
        {showSpecialConditionsPicker && (
          <ContractComplianceSpecialConditions
            {...props}
            specialConditions={specialConditions}
            onSpecialConditionsUpdated={onSpecialConditionsUpdated}
          />
        )}
      </AgxSpacer>
    )
  }

  return <TaskModalStepToggle {...props} step={step} />
}

const ContractCompliancePage = (props: Omit<ContractComplianceStepProps, 'step'>) => {
  const taskSteps = props.task.taskSteps ?? []
  const isGeneralComplianceTask =
    props.task.category?.taskType === TaskCategoryTaskType.GeneralContractCompliance
  const visibleTaskSteps = taskSteps.filter(
    (step) =>
      !(
        props.task.category?.taskType === TaskCategoryTaskType.ContractOffer &&
        step.name === BALANCE_DEPOSIT_STEP_NAME
      )
  )

  return (
    <AgxSpacer direction="vertical" fill>
      <AgxTypography
        id="contractComplianceTaskTitle"
        type={'label'}
        size={'large'}
        color="var(--text-primary)"
        style={{ marginBottom: '32px' }}
      >
        {`Step ${props.taskNumber}: ${getTaskTypeLabel(props.task.category?.taskType)}`}
      </AgxTypography>
      {isGeneralComplianceTask ? (
        <AgxSpacer direction="vertical" fill gap="spacing-xl">
          {taskSteps.map((step, index) => (
            <AgxSpacer
              key={`${step.order}-${step.name}`}
              direction="vertical"
              fill
              gap="spacing-xl"
            >
              <ContractComplianceStep {...props} step={step} />
              {index < taskSteps.length - 1 && (
                <AgxDivider variant="horizontal" />
              )}
            </AgxSpacer>
          ))}
        </AgxSpacer>
      ) : (
        <AgxSpacer direction="vertical" fill gap="spacing-xl">
          {visibleTaskSteps.map((step, index) => (
            <AgxSpacer
              key={`${step.order}-${step.name}`}
              direction="vertical"
              fill
              gap="spacing-xl"
            >
              <ContractComplianceStep
                {...props}
                step={step}
              />
              {index < visibleTaskSteps.length - 1 && (
                <AgxDivider variant="horizontal" />
              )}
            </AgxSpacer>
          ))}
        </AgxSpacer>
      )}
    </AgxSpacer>
  )
}

export default ContractCompliancePage
